/**
 * Entry point: observability first, then the Phaser game.
 *
 * The scale config is US 12.03: a fixed 20×12-tile view (960×576 base) that
 * FITs the window — aspect mismatch letterboxes, ultra-wide monitors see no
 * extra world, phones see the same world smaller. The UI layer is DOM and
 * scales independently.
 */
import Phaser from 'phaser';
import '@ui/design/design.css';
import { worldBus } from '@shared/bus.js';
import { BASE_HEIGHT, BASE_WIDTH } from '@shared/constants.js';
import { sessionState } from '@shared/session-state.js';
import { initObservability, log } from '@transport/observability.js';
import { callDialogue, connection, ensureSession, flushEphemeral } from '@transport/api.js';
import { initConnectionIndicator } from '@ui/connection-indicator.js';
import { initHud } from '@ui/hud.js';
import { initDialoguePanel } from '@ui/dialogue-panel.js';
import { initInventoryPanel } from '@ui/inventory-panel.js';
import { initJournalPanel } from '@ui/journal-panel.js';
import { initApartmentEditor } from '@ui/apartment-editor.js';
import { initAlbumPanel } from '@ui/album-panel.js';
import { initFishingPanel } from '@ui/fishing-panel.js';
import { initTouchControls } from '@ui/touch-controls.js';
import { initFeedback } from '@ui/feedback.js';
import { CityScene } from '@world/city-scene.js';

initObservability();

// UI layer (DOM). The world layer below never reaches outside its canvas.
const ui = document.querySelector<HTMLDivElement>('#ui');
if (ui) {
  const inventory = initInventoryPanel(ui);
  initDialoguePanel(ui, () => inventory.refresh(), inventory.itemName);
  initConnectionIndicator(ui);
  initHud(ui);
  initJournalPanel(ui, () => inventory.refresh());
  initApartmentEditor(ui);
  initAlbumPanel(ui);
  initTouchControls(ui);
  initFeedback(ui);
  initFishingPanel(ui, () => inventory.refresh());
  inventory.refresh(); // first sync feeds the HUD and the bag sprite
}

// Transport ↔ world glue (US 10.02/10.03): the tier tracker feeds the bus,
// and the 5s ephemeral flush doubles as the idle heartbeat.
connection.onChange((tier) => worldBus.emit('connection-tier', { tier }));

let lastPosition: { x: number; y: number } | null = null;
worldBus.on('player-position', (p) => (lastPosition = p));

const flushTimer = setInterval(() => {
  if (lastPosition) flushEphemeral({ position: lastPosition });
}, 5000);
// A locked tab must stop writing entirely (US 15.05) — its position would
// fight the live tab's.
worldBus.on('session-locked', () => clearInterval(flushTimer));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && lastPosition) {
    flushEphemeral({ position: lastPosition }, true); // keepalive write
  }
});

// Boot: session, then crash/tab-close recovery from the saved position.
void ensureSession().then(async (token) => {
  if (!token) {
    log({ level: 'warn', msg: 'no session available — server play disabled' });
    const notice = document.createElement('div');
    notice.className = 'wca-notice';
    notice.textContent = 'Server unavailable — exploring offline. NPCs will not talk.';
    ui?.appendChild(notice);
    return;
  }
  // claim: this boot becomes THE active session (US 15.05); any other open
  // tab gets SESSION_LOCKED on its next call and freezes.
  const sync = await callDialogue<{ ephemeral?: { position?: { x: number; y: number } } }>({
    op: 'sync',
    claim: true,
  });
  const saved = sync.ok ? sync.result.ephemeral?.position : undefined;
  if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
    sessionState.restorePosition = saved;
  }
});

new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'world',
  width: BASE_WIDTH,
  height: BASE_HEIGHT,
  backgroundColor: '#000000',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    pixelArt: true, // crisp scaling for the flat-colour placeholders
  },
  scene: [CityScene],
});

log({
  level: 'warn',
  msg: 'boot ok',
  ctx: {
    release: import.meta.env.VITE_RELEASE ?? 'dev',
    env: import.meta.env.VITE_APP_ENV ?? 'development',
  },
});
